import React, { useState } from "react";
import { Icon } from "@iconify/react";
import Property from "../../components/property";
import Logo from "../../components/assets/logo.png";
import { useAuth } from "../../hooks/useAuth";
import { useSettings } from "../../hooks/useSettings";
import { useNavigate } from "react-router-dom";
import { EventManagement } from "../eventManagement";
import ManageBlog from "../ManageBlog";
import { AgentDashboard } from "../agentDashboard";
import CommunityManagePage from "../communityManager";
import { FloorPlanManagement } from "../FloorPlanManagement";
import { DocManagement } from "../docManagement";
import ManageBoardMembers from "../boardMembers";
import { LeadsManagement } from "../LeadsManagement";
import AgentSponsorsTab from "../AgentSponsorsTab";
import AgentInvestmentsTab from "../AgentInvestmentsTab";
import { useLocation } from "react-router-dom";

export const AgentAdmin = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialTab = params.get("tab") || "Listings";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { logout } = useAuth();
  const { settings } = useSettings();
  const communityName = settings?.clubName || "Admin";
  const navigate = useNavigate();

  const handleLogOut = async () => {
    logout();
    navigate("/");
  };
  const renderContent = () => {
    switch (activeTab) {
      case "Listings":
        return <Property />;
      case "Community":
        return <CommunityManagePage />;
      case "News":
        return <ManageBlog />;
      case "Leads":
        return <LeadsManagement />;
      case "Board":
        return <ManageBoardMembers />;
      case "Calendar":
        return <EventManagement />;
      case "Floor Plans":
        return <FloorPlanManagement />;
      case "Condo Docs":
        return <DocManagement />;
      case "Dashboard":
        return <AgentDashboard />;
      case "Sponsors":
        return <AgentSponsorsTab />;
      case "Investments":
        return <AgentInvestmentsTab />;
      default:
        return <Property />;
    }
  };

  // Function to handle sidebar toggle
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <div className="flex flex-col h-screen font-Poppins lg:flex-row">
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={toggleSidebar}
        ></div>
      )}

      <aside
        className={`
          fixed 
          top-0 
          left-0 
          h-full 
          bg-red-600 
          text-white 
          shadow-md 
          w-52
          transform 
          ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} 
          transition-transform duration-300 
          z-30 
          lg:static 
          lg:translate-x-0 
          lg:w-52
        `}
      >
        {/* Logo and Title and close button on mobile */}
        <div className="flex items-center justify-between py-6 px-4">
          <div className="flex items-center">
            <img src={Logo} className="w-12 h-12 object-contain" alt="Logo" />
            <span className="text-lg font-semibold hidden lg:inline-block">
              {communityName}
            </span>
          </div>
          {/* Close button visible only on mobile */}
          <button
            className="lg:hidden text-white focus:outline-none"
            onClick={toggleSidebar}
            aria-label="Close Sidebar"
          >
            <Icon icon="mdi:close" className="text-2xl" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-grow space-y-2 px-2">
          {[
            { name: "Listings", icon: "mdi:home" },
            { name: "Community", icon: "mdi:account-group" },
            { name: "News", icon: "mdi:blog" },
            { name: "Leads", icon: "mdi:account-multiple-check" },
            { name: "__separator__" },
            { name: "Board", icon: "mdi:account-group" },
            { name: "Calendar", icon: "mdi:events" },
            { name: "Floor Plans", icon: "mdi:floor-plan" },
            { name: "Condo Docs", icon: "mdi:document" },
            { name: "Sponsors", icon: "mdi:hand-heart" },
            { name: "Investments", icon: "mdi:chart-line" },
            { name: "__separator2__" },
            { name: "Dashboard", icon: "material-symbols:dashboard-rounded" },
          ].map(({ name, icon }) =>
            name.startsWith("__separator") ? (
              <hr
                key={name}
                style={{
                  border: "none",
                  borderTop: "2px solid rgba(0,0,0,0.2)",
                  margin: "0.5rem 0",
                }}
              />
            ) : (
              <button
                key={name}
                onClick={() => {
                  setActiveTab(name);
                  setIsSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-4 px-4 py-3 rounded-lg text-sm font-medium transition ${activeTab === name
                  ? "bg-red-500 text-white"
                  : "hover:bg-red-700 hover:text-white"
                  }`}
                aria-current={activeTab === name ? "page" : undefined}
              >
                <Icon icon={icon} className="text-xl" />
                <span className="ml-2">{name}</span>
              </button>
            )
          )}
          {/* Logout Button */}
          <button
            onClick={handleLogOut}
            className="w-full mx-auto space-y-2 flex items-center gap-4 px-4 py-3 text-sm rounded-md font-medium text-white hover:bg-red-700 hover:text-white transition"
          >
            <Icon icon="mdi:logout" className="text-xl" />
            <span className="ml-2">Logout</span>
          </button>
        </nav>
      </aside>

      {/* Top Navigation Bar for Mobile */}
      <header className="flex items-center justify-between bg-red-600 text-white p-4 lg:hidden">
        <button
          onClick={toggleSidebar}
          className="focus:outline-none"
          aria-label="Open Sidebar"
        >
          <Icon icon="mdi:menu" className="text-2xl" />
        </button>
        <div className="flex items-center">
          <img src={Logo} className="w-8 h-8 object-contain" alt="Logo" />
          <span className="ml-2 text-lg font-semibold">{communityName}</span>
        </div>
        {/* Placeholder for alignment */}
        <div className="w-8 h-8"></div>
      </header>

      {/* Main Content */}
      <main className="flex-1 bg-gray-100 overflow-auto">
        <section className="rounded-lg">{renderContent()}</section>
      </main>
    </div>
  );
};
